// src/pages/QuestionnairePage/steps/Step3Sensitive.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AlertCircle, Check, X } from 'lucide-react';

const StepCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 24px;
  padding: 32px 20px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  @media (max-width: 375px) {
    padding: 28px 16px;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0);
    }
  }
`;

const Question = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 24px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #7B68EE;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    svg {
      color: #C4B5FD;
    }
  }
`;

const YesNoGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ChipButton = styled(motion.button)
  .attrs({ type: 'button' })
  .withConfig({
    shouldForwardProp: (prop) => !['$active'].includes(prop),
  })<{ $active: boolean }>`
  flex: 1;
  padding: 20px 24px !important;
  border-radius: 16px !important;
  border: 2px solid ${props => props.$active
    ? '#7B68EE'
    : 'rgba(123, 104, 238, 0.2)'
  } !important;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)'
    : 'rgba(255, 255, 255, 0.92)'
  } !important;
  color: ${props => props.$active
    ? '#FFFFFF !important'
    : '#000000 !important'
  };
  font-size: 16px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  box-shadow: ${props => props.$active
    ? '0 8px 22px rgba(123, 104, 238, 0.35)'
    : '0 4px 12px rgba(15, 23, 42, 0.08)'
  } !important;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  outline: none !important;

  svg {
    color: ${props => props.$active ? '#FFFFFF !important' : '#7B68EE !important'};
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #7B68EE !important;
    box-shadow: 0 10px 28px rgba(123, 104, 238, 0.35) !important;
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: ${props => props.$active
      ? '#C4B5FD'
      : 'rgba(157, 142, 238, 0.3)'
    } !important;
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #9d8eee, #b896dc)'
      : 'rgba(255, 255, 255, 0.08)'
    } !important;
    color: ${props => props.$active
      ? '#FFFFFF !important'
      : '#FFFFFF !important'
    };
    box-shadow: ${props => props.$active
      ? '0 8px 22px rgba(157, 142, 238, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.3)'
    } !important;

    svg {
      color: ${props => props.$active ? '#FFFFFF !important' : '#C4B5FD !important'};
    }

    &:hover {
      border-color: #C4B5FD !important;
      box-shadow: 0 10px 28px rgba(157, 142, 238, 0.45) !important;
    }
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonLabel = styled.span`
  font-size: 15px;
`;

interface Step3SensitiveProps {
  value: boolean | null;
  onChange: (sensitive: boolean) => void;
}

export const Step3Sensitive: React.FC<Step3SensitiveProps> = ({ value, onChange }) => {
  return (
    <StepCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Question>
        <AlertCircle size={24} />
        Считаете ли Вы свою кожу чувствительной?
      </Question>
      
      <YesNoGroup>
        <ChipButton
          $active={value === true}
          onClick={() => onChange(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <Check size={28} />
          </ButtonIcon>
          <ButtonLabel>Да</ButtonLabel>
        </ChipButton>
        
        <ChipButton
          $active={value === false}
          onClick={() => onChange(false)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <X size={28} />
          </ButtonIcon>
          <ButtonLabel>Нет</ButtonLabel>
        </ChipButton>
      </YesNoGroup>
    </StepCard>
  );
};